import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Euro, LogOut, FileText, Zap } from "lucide-react";
import { Button } from "../../components/ui/button";
import { getCurrentAdminEmail, initialsFromEmail } from "./currentAdmin";

export default function AdminShell({ children, title, actions }) {
  const nav = useNavigate();
  const loc = useLocation();
  const currentEmail = getCurrentAdminEmail();
  const initials = initialsFromEmail(currentEmail);
  const logout = () => {
    localStorage.removeItem("cc_admin_token");
    nav("/admin/login");
  };

  const items = [
    { to: "/admin", label: "Richieste", icon: LayoutDashboard },
    { to: "/admin/rates", label: "Tariffe €/mq", icon: Euro },
    { to: "/admin/ape-requests", label: "Servizio APE", icon: Zap },
  ];

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex flex-col w-64 bg-slate-900 text-white p-6 sticky top-0 h-screen self-start" data-testid="admin-sidebar">
        <div className="mb-8">
          <img
            src="/logo-unioncasa-white.png"
            alt="UnionCasa"
            className="w-48 mb-4 select-none"
            data-testid="admin-sidebar-logo"
          />
          <div className="font-serif text-xl font-light leading-tight">
            Canone <br /> Concordato
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {items.map((i) => {
            const Icon = i.icon;
            const active = loc.pathname === i.to || (i.to === "/admin" && loc.pathname.startsWith("/admin/submissions"));
            return (
              <Link
                key={i.to}
                to={i.to}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  active ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`nav-${i.label.toLowerCase()}`}
              >
                <Icon size={16} /> {i.label}
              </Link>
            );
          })}
        </nav>
        <div className="pt-6 border-t border-white/10 space-y-3">
          {currentEmail && (
            <div className="flex items-center gap-3 px-2" data-testid="admin-current-user">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-xs font-bold" data-testid="admin-current-initials">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 leading-tight">
                  Loggato come
                </p>
                <p
                  className="text-xs text-white font-medium truncate leading-tight"
                  title={currentEmail}
                  data-testid="admin-current-email"
                >
                  {currentEmail}
                </p>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 text-slate-400 hover:text-white text-sm"
            data-testid="admin-logout"
          >
            <LogOut size={16} /> Esci
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 bg-white border-b border-slate-200 z-10">
          <div className="px-6 md:px-10 py-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <FileText size={20} className="text-slate-400 lg:hidden" />
              <h1 className="font-serif text-xl md:text-2xl text-slate-900 truncate">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              {actions}
              <Button variant="ghost" size="sm" onClick={logout} className="lg:hidden" data-testid="admin-logout-mobile">
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </header>
        <div className="p-6 md:p-10">{children}</div>
      </main>
    </div>
  );
}
